import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Heading } from '@insolech/ui';
import { supabase } from '@/lib/supabase';

/**
 * AuthCallbackPage — handles the redirect target for:
 *   - Magic-link sign-in (?type=magiclink&token_hash=...)
 *   - Email confirmation (?type=signup&token_hash=...)
 *   - Recovery / password reset (?type=recovery&token_hash=...)
 *
 * The PKCE flow used by the browser client (`detectSessionInUrl: true`) usually
 * exchanges the code automatically, but the OTP-style links carry a `token_hash`
 * that needs `verifyOtp`. We try both paths so all four entry routes converge here.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tokenHash = searchParams.get('token_hash');
        const type = searchParams.get('type') as
          | 'magiclink'
          | 'signup'
          | 'recovery'
          | 'invite'
          | 'email_change'
          | null;

        // PKCE auto-exchange — already-signed-in case
        const existing = await supabase.auth.getSession();
        if (existing.data.session) {
          if (cancelled) return;
          if (type === 'recovery') navigate('/auth/reset', { replace: true });
          else navigate('/dashboard', { replace: true });
          return;
        }

        if (tokenHash && type) {
          const { error: vErr } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type,
          });
          if (vErr) throw vErr;
          if (cancelled) return;
          if (type === 'recovery') navigate('/auth/reset', { replace: true });
          else navigate('/dashboard', { replace: true });
          return;
        }

        // No usable parameters — bounce to /auth
        if (!cancelled) navigate('/auth', { replace: true });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Sign-in failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-surface-base px-6">
      <div className="max-w-md w-full text-center">
        <Heading as="h1" size="h3">
          Verifying your link…
        </Heading>
        <p className="mt-2 text-text-secondary text-sm">
          Hold on while we sign you in. This usually takes a second.
        </p>
        {error ? (
          <Alert tone="error" title="Could not verify the link" className="mt-6 text-start">
            {error}
            <div className="mt-3">
              <a className="text-sm text-brand-700 hover:underline" href="/auth">
                Back to sign in
              </a>
            </div>
          </Alert>
        ) : (
          <div className="mt-6 inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500" />
        )}
      </div>
    </main>
  );
}
